import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import axios from 'axios';
import { CloseCode } from 'graphql-ws';

import i18next from '@/i18n';

/** Коды ошибок в `extensions.code` GraphQL-ответа (см. docs/api.md, «Ошибки»). */
export const API_ERROR_CODES = {
  unauthenticated: 'UNAUTHENTICATED',
  forbidden: 'FORBIDDEN',
  notFound: 'NOT_FOUND',
  /** Валидация входа на бэкенде (NestJS ValidationPipe): текст — технический, на английском. */
  badRequest: 'BAD_REQUEST',
  /** Значения переменных не подходят под типы схемы. */
  badUserInput: 'BAD_USER_INPUT',
  /** Запрос не соответствует схеме. */
  validationFailed: 'GRAPHQL_VALIDATION_FAILED',
  internal: 'INTERNAL_SERVER_ERROR'
} as const;

/**
 * Отказ из-за входных данных. Формы проверяют поля сами, так что такой отказ —
 * расхождение с бэкендом, а не ошибка сотрудника: его технический текст не показываем.
 */
const INPUT_ERROR_CODES: ReadonlySet<string> = new Set([
  API_ERROR_CODES.badRequest,
  API_ERROR_CODES.badUserInput,
  API_ERROR_CODES.validationFailed
]);

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_SERVER_ERROR = 500;

/** Штатное закрытие WebSocket (RFC 6455). */
const WS_NORMAL_CLOSURE = 1000;
/** Код, которым graphql-ws закрывает сокет при `client.terminate()`. */
const WS_TERMINATED = 4499;

/** HTTP-статус неуспешного ответа: из `ServerError` Apollo или ответа axios (REST). */
const getHttpStatus = (error: unknown): number | undefined => {
  if (ServerError.is(error)) return error.statusCode;
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
};

/** Первый `extensions.code` среди GraphQL-ошибок ответа. */
const getGraphQLErrorCode = (error: unknown): string | undefined => {
  if (!CombinedGraphQLErrors.is(error)) return undefined;

  const code = error.errors.find(
    (graphQLError) => typeof graphQLError.extensions?.code === 'string'
  )?.extensions?.code;

  return typeof code === 'string' ? code : undefined;
};

/** Текст ошибки REST-ответа 4xx из тела `{ message }`, если бэкенд его прислал. */
const getRestErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError<{ message?: unknown }>(error)) return undefined;

  const message = error.response?.data?.message;
  return typeof message === 'string' && message ? message : undefined;
};

/** Запрос не дошёл до сервера: нет сети, CORS, сервер недоступен. */
const isNetworkError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) return !error.response;
  // fetch (HttpLink Apollo) при сетевой ошибке бросает TypeError.
  return error instanceof TypeError;
};

/** HTTP-статус исходного исключения бэкенда (NestJS кладёт его в `extensions.originalError`). */
const getOriginalStatusCode = (error: unknown): number | undefined => {
  if (!CombinedGraphQLErrors.is(error)) return undefined;

  for (const graphQLError of error.errors) {
    const original = graphQLError.extensions?.originalError;
    if (
      typeof original === 'object' &&
      original !== null &&
      'statusCode' in original &&
      typeof original.statusCode === 'number'
    ) {
      return original.statusCode;
    }
  }
  return undefined;
};

/** Запрошенной записи нет: код `NOT_FOUND` или исходный статус 404. */
export const isNotFoundError = (error: unknown): boolean =>
  getGraphQLErrorCode(error) === API_ERROR_CODES.notFound ||
  getOriginalStatusCode(error) === HTTP_NOT_FOUND;

/** Сервер не признал токен: HTTP 401 или GraphQL-ошибка `UNAUTHENTICATED`. */
export const isUnauthenticatedError = (error: unknown): boolean =>
  getHttpStatus(error) === HTTP_UNAUTHORIZED ||
  getGraphQLErrorCode(error) === API_ERROR_CODES.unauthenticated;

/** Запрос отменён (AbortController / размонтирование) — пользователю не показываем. */
export const isCancelledError = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (error instanceof DOMException && error.name === 'AbortError');

/**
 * Текст ошибки API для пользователя (notification.error): одинаково для
 * GraphQL и REST. Внутренние ошибки сервера не раскрываются — общий текст.
 */
export const getApiErrorMessage = (error: unknown): string => {
  const generic = i18next.t('errors.generic');
  const code = getGraphQLErrorCode(error);
  const status = getHttpStatus(error);

  if (
    code === API_ERROR_CODES.unauthenticated ||
    status === HTTP_UNAUTHORIZED
  ) {
    return i18next.t('errors.api.unauthenticated');
  }
  if (code === API_ERROR_CODES.forbidden || status === HTTP_FORBIDDEN) {
    return i18next.t('errors.api.forbidden');
  }

  if (CombinedGraphQLErrors.is(error)) {
    if (code === API_ERROR_CODES.internal) return generic;
    if (code && INPUT_ERROR_CODES.has(code)) {
      return i18next.t('errors.api.badRequest');
    }
    return error.errors[0]?.message || generic;
  }

  if (status !== undefined) {
    return status < HTTP_SERVER_ERROR
      ? (getRestErrorMessage(error) ?? generic)
      : generic;
  }

  if (isNetworkError(error)) return i18next.t('errors.api.network');

  return generic;
};

const getCloseCode = (event: unknown): unknown =>
  typeof event === 'object' && event !== null && 'code' in event
    ? event.code
    : undefined;

/** Сервер закрыл сокет подписок из-за невалидного или просроченного токена. */
export const isAuthCloseEvent = (event: unknown): boolean => {
  const code = getCloseCode(event);
  return code === CloseCode.Unauthorized || code === CloseCode.Forbidden;
};

/**
 * Сокет закрыт намеренно: ленивое соединение без подписок или `terminate()`
 * при выходе. Связь не потеряна — баннер «нет связи» не нужен.
 */
export const isIntentionalCloseEvent = (event: unknown): boolean => {
  const code = getCloseCode(event);
  return code === WS_NORMAL_CLOSURE || code === WS_TERMINATED;
};
